import React, { memo, useCallback } from 'react'
import { useFormik } from 'formik'
import { useDispatch } from 'react-redux'
import * as yup from 'yup'

import { useShallowEqualSelector } from 'custom-hooks'

import InputAdornment from '@material-ui/core/InputAdornment'
import TextInput from 'components/CommonUI/TextInput/TextInput'
import Button from 'components/CommonUI/Button/Button'
import TitleSection from './TitleSection'
import {
  changeCurrentPassword,
  getChangePasswordErrorCodeSelector,
  logout,
} from 'redux/reducers/profile'
import { routingConstants } from 'constants/routingConstants'

const profileSecuritySchema = yup.object().shape({
  oldPassword: yup.string().required(),
  newPassword: yup.string().when('oldPassword', {
    is: (field) => !!field,
    then: yup
      .string()
      .notOneOf(
        [yup.ref('oldPassword'), null],
        'New password not should be the same as the current password'
      )
      .required()
      .min(8)
      .max(20),
    otherwise: yup.string().min(8).max(20),
  }),
  repeatPassword: yup.string().when(['oldPassword', 'newPassword'], {
    is: (...fields) => fields.some(Boolean),
    then: yup
      .string()
      .oneOf([yup.ref('newPassword'), null], 'Repeat password must match')
      .required(),
    otherwise: yup.string(),
  }),
})

const ProfileSecurityForm = () => {
  const dispatch = useDispatch()

  const errorCode = useShallowEqualSelector(getChangePasswordErrorCodeSelector)

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      repeatPassword: '',
    },
    onSubmit: (values) => {
      dispatch(
        changeCurrentPassword(
          {
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
            callback: formik.resetForm,
          }
          //formik.resetForm
        )
      )
    },
    validationSchema: profileSecuritySchema,
  })

  const handleErrorCode = useCallback(() => {
    switch (errorCode) {
      case 'NotAuthorizedException':
        return 'Incorrect current password.'

      default:
        return 'Something went wrong'
    }
  }, [errorCode])

  const handleForgotPasswordClick = useCallback(
    (e) => {
      e.stopPropagation()
      dispatch(logout(routingConstants.recoveryPassword))
    },
    [dispatch]
  )

  return (
    <form
      className="security_section form_section"
      onSubmit={formik.handleSubmit}
    >
      <TitleSection
        title="Security"
        errorMessage={errorCode && handleErrorCode()}
      />
      <div className="form_row">
        <TextInput
          label="Old password"
          classNameContainer="profile_form_input"
          type="password"
          name="oldPassword"
          onChange={formik.handleChange}
          value={formik.values.oldPassword}
          autoComplete="new-password"
          onBlur={formik.handleBlur}
          errorMessage={formik.touched.oldPassword && formik.errors.oldPassword}
          endAdornment={
            <InputAdornment position="start">
              <button
                className="forgot_button"
                onClick={handleForgotPasswordClick}
                type="button"
              >
                Forgot Password?
              </button>
            </InputAdornment>
          }
        />
      </div>
      <div className="form_row">
        <TextInput
          label="New password"
          classNameContainer="profile_form_input"
          type="password"
          name="newPassword"
          onChange={formik.handleChange}
          value={formik.values.newPassword}
          errorMessage={formik.touched.newPassword && formik.errors.newPassword}
          onBlur={formik.handleBlur}
        />
      </div>
      <div className="form_row">
        <TextInput
          label="Repeat password"
          classNameContainer="profile_form_input"
          type="password"
          name="repeatPassword"
          onChange={formik.handleChange}
          value={formik.values.repeatPassword}
          errorMessage={
            formik.touched.repeatPassword && formik.errors.repeatPassword
          }
          onBlur={formik.handleBlur}
        />
      </div>
      <div className="save_button_container">
        <Button
          className="save_changed_button"
          type="submit"
          disabled={
            !(
              formik.values.oldPassword &&
              formik.values.newPassword &&
              formik.values.repeatPassword &&
              formik.isValid
            )
          }
        >
          Save changes
        </Button>
      </div>
    </form>
  )
}

export default memo(ProfileSecurityForm)
